import {
   ReportRowRiskMetadata,
   ReportRowRiskSeverity,
   ReportRowRiskState
} from '../models/report-row-risk';
import { ReportRowVm } from '../models/report-row-vm';
import {
   getReportRowRiskMetadata,
   isRecord,
   parseReportJson,
   ReportJsonObject
} from './report-json.utils';
import { getRealTyallsRowId } from './report-row-id.utils';

const SEVERITY_ORDER: Record<ReportRowRiskSeverity, number> = {
   CRITICAL: 6,
   HIGH: 5,
   MEDIUM: 4,
   WARNING: 3,
   LOW: 2,
   INFO: 1
};

const RECURSION_SKIP_PROPERTIES = new Set([
   '_Meta',
   'RiskSummary',
   'Risks',
   'Metadata',
   'Parameters'
]);

/**
 * Reads one generated JSON source row and returns its display-ready risk state.
 * Highlight/category/severity fields alone do not invent a risk.
 */
export function extractReportRowRiskState(
   sourceRow: unknown
): ReportRowRiskState | null {
   const metadata = getReportRowRiskMetadata(sourceRow);

   if (!metadata || !hasRiskEvidence(metadata)) {
      return null;
   }

   const riskDetails = metadata.Risks ?? [];
   const riskCodes = deduplicateStrings([
      ...(metadata.RiskCodes ?? []),
      ...riskDetails.map((risk) => risk.RiskCode)
   ]);
   const messages = deduplicateStrings(riskDetails.map((risk) => risk.Message));

   return {
      hasRisk: true,
      highlight:
         metadata.Highlight === true ||
         riskDetails.some((risk) => risk.Highlight === true),
      riskCodes,
      messages,
      severity: resolveHighestSeverity([
         metadata.RiskSeverity,
         ...riskDetails.map((risk) => risk.Severity)
      ]),
      isFinancial:
         metadata.IsFinancial === true ||
         riskDetails.some((risk) => risk.IsFinancial === true),
      isOperations:
         metadata.IsOperations === true ||
         riskDetails.some((risk) => risk.IsOperations === true),
      sourceRiskCount: 1
   };
}

/**
 * Combines generated JSON risk metadata from multiple source rows into the
 * single risk state required by a grouped/calculated displayed row.
 */
export function combineReportRowRisks(
   sourceRows: readonly unknown[]
): ReportRowRiskState | null {
   return combineReportRowRiskStates(
      sourceRows.map((sourceRow) => extractReportRowRiskState(sourceRow))
   );
}

export function combineReportRowRiskStates(
   states: readonly (ReportRowRiskState | null | undefined)[]
): ReportRowRiskState | null {
   const presentStates = states.filter(
      (state): state is ReportRowRiskState => state?.hasRisk === true
   );

   if (!presentStates.length) {
      return null;
   }

   return {
      hasRisk: true,
      highlight: presentStates.some((state) => state.highlight),
      riskCodes: deduplicateStrings(
         presentStates.flatMap((state) => state.riskCodes)
      ),
      messages: deduplicateStrings(presentStates.flatMap((state) => state.messages)),
      severity: resolveHighestSeverity(presentStates.map((state) => state.severity)),
      isFinancial: presentStates.some((state) => state.isFinancial),
      isOperations: presentStates.some((state) => state.isOperations),
      sourceRiskCount: presentStates.reduce(
         (total, state) => total + state.sourceRiskCount,
         0
      )
   };
}

/**
 * Attaches risk state to flattened report rows by matching their source row
 * IDs against TyallsRowId values found in the generated report JSON.
 */
export function attachReportRowRisks(
   reportJson: unknown,
   rows: readonly ReportRowVm[]
): ReportRowVm[] {
   const riskBySourceRowId = buildSourceRowRiskIndex(reportJson);

   return rows.map((row) => {
      const sourceRowIds = getDisplayedRowSourceIds(row);
      const indexedStates = sourceRowIds.map((sourceRowId) =>
         riskBySourceRowId.get(sourceRowId)
      );
      const risk = combineReportRowRiskStates([row.risk, ...indexedStates]);

      if (!risk) {
         return row;
      }

      return {
         ...row,
         risk
      };
   });
}

export function getReportRowRiskSeverityClass(
   severity: ReportRowRiskSeverity | null | undefined
): string | null {
   return severity ? severity.toLowerCase() : null;
}

export function getReportRowRiskSortRank(
   risk: ReportRowRiskState | null | undefined
): number {
   if (!risk?.hasRisk) {
      return 99;
   }

   switch (risk.severity) {
      case 'CRITICAL':
         return 1;
      case 'HIGH':
         return 2;
      case 'MEDIUM':
         return 3;
      case 'WARNING':
         return 4;
      case 'LOW':
         return 5;
      case 'INFO':
         return 6;
      default:
         return 7;
   }
}

function hasRiskEvidence(metadata: ReportRowRiskMetadata): boolean {
   return (
      metadata.HasRisk === true ||
      deduplicateStrings(metadata.RiskCodes ?? []).length > 0 ||
      (metadata.Risks?.length ?? 0) > 0
   );
}

function buildSourceRowRiskIndex(
   reportJson: unknown
): Map<string, ReportRowRiskState> {
   const riskBySourceRowId = new Map<string, ReportRowRiskState>();
   const visited = new WeakSet<object>();

   visitReportValue(parseReportJson(reportJson), riskBySourceRowId, visited);

   return riskBySourceRowId;
}

function visitReportValue(
   value: unknown,
   riskBySourceRowId: Map<string, ReportRowRiskState>,
   visited: WeakSet<object>
): void {
   if (Array.isArray(value)) {
      if (visited.has(value)) {
         return;
      }

      visited.add(value);

      for (const item of value) {
         visitReportValue(item, riskBySourceRowId, visited);
      }

      return;
   }

   if (!isRecord(value)) {
      return;
   }

   if (visited.has(value)) {
      return;
   }

   visited.add(value);
   indexSourceRowRisk(value, riskBySourceRowId);

   for (const [propertyName, propertyValue] of Object.entries(value)) {
      if (RECURSION_SKIP_PROPERTIES.has(propertyName)) {
         continue;
      }

      visitReportValue(propertyValue, riskBySourceRowId, visited);
   }
}

function indexSourceRowRisk(
   sourceRow: ReportJsonObject,
   riskBySourceRowId: Map<string, ReportRowRiskState>
): void {
   const sourceRowId = getRealTyallsRowId(sourceRow);

   if (!sourceRowId) {
      return;
   }

   const risk = extractReportRowRiskState(sourceRow);

   if (!risk) {
      return;
   }

   const combinedRisk = combineReportRowRiskStates([
      riskBySourceRowId.get(sourceRowId),
      risk
   ]);

   if (combinedRisk) {
      riskBySourceRowId.set(sourceRowId, {
         ...combinedRisk,
         sourceRiskCount: 1
      });
   }
}

function getDisplayedRowSourceIds(row: ReportRowVm): string[] {
   const sourceRowIds = row.sourceRowIds?.length ? row.sourceRowIds : [row.rowId];

   return deduplicateStrings(sourceRowIds);
}

function resolveHighestSeverity(
   values: readonly (string | null | undefined)[]
): ReportRowRiskSeverity | null {
   let highestSeverity: ReportRowRiskSeverity | null = null;
   let highestRank = 0;

   for (const value of values) {
      const severity = normaliseSeverity(value);

      if (!severity) {
         continue;
      }

      const rank = SEVERITY_ORDER[severity];

      if (rank > highestRank) {
         highestSeverity = severity;
         highestRank = rank;
      }
   }

   return highestSeverity;
}

function normaliseSeverity(
   value: string | null | undefined
): ReportRowRiskSeverity | null {
   const normalisedValue = value?.trim().toUpperCase();

   switch (normalisedValue) {
      case 'CRITICAL':
      case 'HIGH':
      case 'MEDIUM':
      case 'WARNING':
      case 'LOW':
      case 'INFO':
         return normalisedValue;
      default:
         return null;
   }
}

function deduplicateStrings(
   values: readonly (string | null | undefined)[]
): string[] {
   const seen = new Set<string>();
   const result: string[] = [];

   for (const value of values) {
      const normalisedValue = value?.trim();

      if (!normalisedValue || seen.has(normalisedValue)) {
         continue;
      }

      seen.add(normalisedValue);
      result.push(normalisedValue);
   }

   return result;
}
